import React, {useEffect, useRef, useState} from 'react';
import {COMPLETE_BUTTON_CHECKS, COMPLETE_BUTTON_MODES} from '../../../utils/gameUi/getCompleteButtonView';
import {joinClassNames} from '../../gameUi/joinClassNames';

// Части плиты кнопки хода (вариант «А · Плита», прототип этапа 0 в
// openspec/changes/archive/2026-10-23-phase-progress-button/prototype)

// Размер и срез углов плиты — те же, что в CurrentPlayerSection.css (width,
// height, --gui-cut): по ним строится контур кольца таймера. Срез здесь и там
// меняется вместе — иначе углы кольца уйдут под clip-path плиты
const PLATE_WIDTH = 72;
const PLATE_HEIGHT = 110;
const PLATE_CUT = 9;
const RING_INSET = 1.5;

/**
 * Иконка-маска: PNG красится цветом подписи (currentColor или переменной в CSS)
 */
export const CompleteButtonMaskIcon = ({src, className}) => (
  <i
    className={joinClassNames('complete-phase-button_mask-icon', className)}
    style={{'--complete-button-icon': `url("${src}")`}}
    aria-hidden={true}
  />
);

/**
 * Галочка лежит прямо на эмали. Выгравированная — полупрозрачная темная с
 * бликом, поэтому темнее эмали любой фазы; выпуклая — белая с тенью
 */
export const CompleteButtonCheck = ({kind}) => {
  const isRaised = kind === COMPLETE_BUTTON_CHECKS.RAISED;
  const size = isRaised ? 42 : 38;
  return (
    <svg
      className={`complete-phase-button_check complete-phase-button_check_${kind}`}
      width={size}
      height={size}
      viewBox={'0 0 48 48'}
      aria-hidden={true}
    >
      <path
        d={'M9 25l10 10 20-22'}
        fill={'none'}
        stroke={'currentColor'}
        strokeWidth={6.5}
        strokeLinecap={'round'}
        strokeLinejoin={'round'}
      />
    </svg>
  );
};

/**
 * Счетчик N/S с иконкой дела: в «есть дела» — в темном углублении, в
 * «готово» — на выпуклом золотом жетоне, приглушенный
 */
export const CompleteButtonCounter = ({view}) => (
  <span
    className={joinClassNames(
      'complete-phase-button_counter',
      view.isCounterMuted ? 'complete-phase-button_counter_gold' : 'complete-phase-button_counter_sunk',
      view.isCounterCompact && 'complete-phase-button_counter_compact',
    )}
  >
    <span className={'complete-phase-button_counter-numbers'}>
      <span className={'complete-phase-button_counter-done'}>{view.done}</span>
      <span className={'complete-phase-button_counter-total'}>/{view.total}</span>
    </span>
    {view.deedIcon && <CompleteButtonMaskIcon className={'complete-phase-button_counter-deed'} src={view.deedIcon}/>}
  </span>
);

// Контур плиты со срезанными углами от середины верхней грани по часовой стрелке
const RING_PATH = (() => {
  const i = RING_INSET;
  const c = PLATE_CUT;
  const w = PLATE_WIDTH;
  const h = PLATE_HEIGHT;
  const points = [[w / 2, i], [w - c, i], [w - i, c], [w - i, h - c], [w - c, h - i], [c, h - i], [i, h - c], [i, c], [c, i]];
  return `M${points.map((point) => point.join(' ')).join(' L')} Z`;
})();

/**
 * Остаток времени хода — кольцо по краю плиты, убывает по часовой стрелке
 * @param {number} fraction - доля оставшегося времени, 0..1
 */
export const CompleteButtonTimerRing = ({fraction}) => {
  const percent = (Math.max(0, Math.min(1, fraction)) * 100).toFixed(2);
  return (
    <svg
      className={'complete-phase-button_ring'}
      viewBox={`0 0 ${PLATE_WIDTH} ${PLATE_HEIGHT}`}
      preserveAspectRatio={'none'}
      aria-hidden={true}
      data-fraction={percent}
    >
      <path className={'complete-phase-button_ring-track'} d={RING_PATH}/>
      <path
        className={'complete-phase-button_ring-bar'}
        d={RING_PATH}
        pathLength={100}
        strokeDasharray={`${percent} 100`}
      />
    </svg>
  );
};

/**
 * Подпись кнопки для экранного диктора: что произойдет по нажатию
 */
export const getCompleteButtonLabel = (view) => {
  switch (view.mode) {
    case COMPLETE_BUTTON_MODES.PENDING:
      return view.isCounterShown
        ? `Завершить: обработано ${view.done} из ${view.total}`
        : 'Завершить';
    case COMPLETE_BUTTON_MODES.READY:
      return view.isCounterShown
        ? `Завершить: делать больше нечего, обработано ${view.done} из ${view.total}`
        : 'Завершить: делать больше нечего';
    case COMPLETE_BUTTON_MODES.CONFIRM:
      return 'Подтвердить ближний бой';
    case COMPLETE_BUTTON_MODES.LOCKED:
      return 'Время вышло';
    case COMPLETE_BUTTON_MODES.ENDED:
      return 'Итоги партии';
    default:
      return 'Ожидаем';
  }
};

// Сколько держится класс отклика: дольше самой анимации (0,45 с), но не
// дольше паузы между действиями. Снимается по таймеру, а не по animationend:
// при уменьшении анимации анимаций нет, и событие не пришло бы
const PULSE_DURATION_MS = 600;

export const COMPLETE_BUTTON_PULSES = Object.freeze({
  // Короткий отклик на смену N
  BUMP: 'bump',
  // Однократная вспышка при переходе в «готово»
  FLASH: 'flash',
});

// Вспышка «готово» ждет, удержится ли режим: при автозавершении (снабжение
// раздано автоматически) «готово» мелькает на кадр и сразу сменяется на
// «ждите» — вспышка тогда была бы ложной
const FLASH_HOLD_MS = 300;

/**
 * Отклик кнопки на смену счетчика и переход в «готово». Срабатывает только
 * при смене N или режима — тик таймера их не меняет. Отклик живет, пока
 * кнопка в том режиме, в котором он начался: ушла из него — отклик снят.
 * Неподтвержденный ближний бой загорается, когда потрачен последний
 * переброс, — это тот же переход в «готово», только внутри режима
 * @returns {string|null} COMPLETE_BUTTON_PULSES
 */
export const useCompleteButtonPulse = ({done, mode, isGlowing}) => {
  // {name, mode} — какой отклик и в каком режиме он начался
  const [pulse, setPulse] = useState(null);
  const previousRef = useRef({done, mode, isGlowing});
  // Таймеры — в ref, а не в очистке эффекта: смена режима без отклика иначе
  // отменила бы снятие, и класс отклика остался бы навсегда
  const clearTimeoutRef = useRef(null);
  const flashTimeoutRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(clearTimeoutRef.current);
    clearTimeout(flashTimeoutRef.current);
  }, []);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = {done, mode, isGlowing};
    if (mode !== previous.mode) {
      // Режим сменился раньше, чем вспышка дождалась своего часа
      clearTimeout(flashTimeoutRef.current);
    }

    const startPulse = (name) => {
      setPulse({name, mode});
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = setTimeout(() => setPulse(null), PULSE_DURATION_MS);
    };

    const isBecameReady = mode === COMPLETE_BUTTON_MODES.READY && previous.mode === COMPLETE_BUTTON_MODES.PENDING;
    const isCombatBecameReady = mode === COMPLETE_BUTTON_MODES.CONFIRM && previous.mode === COMPLETE_BUTTON_MODES.CONFIRM
      && isGlowing && !previous.isGlowing;
    if (isBecameReady || isCombatBecameReady) {
      flashTimeoutRef.current = setTimeout(() => startPulse(COMPLETE_BUTTON_PULSES.FLASH), FLASH_HOLD_MS);
    } else if (done !== null && previous.done !== null && done !== previous.done) {
      startPulse(COMPLETE_BUTTON_PULSES.BUMP);
    }
  }, [done, mode, isGlowing]);

  return pulse && pulse.mode === mode ? pulse.name : null;
};
